<template>
  <div class="fb-page bg-light-page">

    <!-- Header -->
    <div class="fb-header d-flex align-items-center">
      <button class="fb-back-btn d-flex align-items-center justify-content-center" type="button" @click="$router.back()">
        <ArrowLeft :size="20" :stroke-width="2.5" />
      </button>
      <h1 class="heading-6-1 mb-0">Feedback</h1>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="fb-center">
      <div class="fb-spinner-ring"></div>
    </div>

    <!-- Load Error -->
    <div v-else-if="loadError" class="fb-center px-4">
      <p class="fb-error-text text-center mb-3">{{ loadError }}</p>
      <button class="fb-submit-btn" type="button" @click="fetchForm">Try Again</button>
    </div>

    <!-- Success -->
    <div v-else-if="screen === 'success'" class="fb-success d-flex flex-column align-items-center px-4">
      <div class="fb-success-icon d-flex align-items-center justify-content-center mb-4">
        <CheckCircle :size="56" :stroke-width="1.5" />
      </div>
      <h2 class="fb-success-title text-center mb-2">Thank You!</h2>
      <p class="fb-success-sub text-center mb-5">Your feedback has been submitted. We appreciate your time.</p>
      <button class="fb-submit-btn w-100" type="button" @click="$router.push({ name: 'ConciergeMainPage' })">
        Back to Home
      </button>
    </div>

    <!-- Form -->
    <template v-else>
      <div class="fb-scroll">

        <!-- Hero -->
        <div v-if="formData && formData.image_url" class="fb-hero" :style="heroStyle">
          <div class="fb-hero-overlay"></div>
          <div class="fb-hero-body px-4 pb-5 pt-4">
            <h2 class="fb-hero-title mb-2">{{ formData.title }}</h2>
            <div v-if="formData.description" class="fb-hero-desc" v-html="formData.description"></div>
          </div>
        </div>

        <!-- Questions -->
        <div class="px-3 pt-4">
          <div
            v-for="(question, idx) in questions"
            :key="question.id"
            class="fb-question-card mb-3 p-3"
          >
            <p class="fb-question-label mb-3">
              {{ idx + 1 }}. {{ question.question_en }}
              <span v-if="question.is_required" class="fb-required"> *</span>
            </p>

            <!-- STAR -->
            <div v-if="question.answer_type === 'STAR'" class="d-flex" style="gap:6px">
              <button
                v-for="i in 5"
                :key="i"
                type="button"
                :class="['fb-star-btn', { 'fb-star-btn--active': i <= answers[question.id] }]"
                @click="setAnswer(question.id, i)"
              >★</button>
            </div>

            <!-- TEXT -->
            <textarea
              v-else-if="question.answer_type === 'TEXT'"
              class="fb-textarea"
              rows="3"
              :placeholder="question.is_required ? 'Your answer' : 'Your answer (optional)'"
              :value="answers[question.id]"
              @input="setAnswer(question.id, $event.target.value)"
            ></textarea>

            <!-- CHOICE (single) -->
            <div v-else-if="question.answer_type === 'CHOICE'" class="d-flex flex-column" style="gap:8px">
              <button
                v-for="opt in question.answer_list"
                :key="opt.id"
                type="button"
                :class="['fb-opt-btn d-flex align-items-center', { 'fb-opt-btn--active': answers[question.id] === opt.id }]"
                @click="setAnswer(question.id, opt.id)"
              >
                <span :class="['fb-radio flex-shrink-0', { 'fb-radio--on': answers[question.id] === opt.id }]"></span>
                <span class="ml-2 fb-opt-label">{{ opt.value_en }}</span>
              </button>
            </div>

            <!-- MULTIPLE_CHOICE -->
            <div v-else-if="question.answer_type === 'MULTIPLE_CHOICE'" class="d-flex flex-column" style="gap:8px">
              <button
                v-for="opt in question.answer_list"
                :key="opt.id"
                type="button"
                :class="['fb-opt-btn d-flex align-items-center', { 'fb-opt-btn--active': isMultiSelected(question.id, opt.id) }]"
                @click="toggleMulti(question.id, opt.id)"
              >
                <span :class="['fb-checkbox flex-shrink-0 d-flex align-items-center justify-content-center', { 'fb-checkbox--on': isMultiSelected(question.id, opt.id) }]">
                  <Check v-if="isMultiSelected(question.id, opt.id)" :size="11" :stroke-width="3.5" />
                </span>
                <span class="ml-2 fb-opt-label">{{ opt.value_en }}</span>
              </button>
            </div>
          </div>

          <p v-if="submitError" class="fb-submit-error mb-3">{{ submitError }}</p>

          <div style="height:90px"></div>
        </div>
      </div>

      <!-- Sticky Submit -->
      <div class="fb-sticky-bar">
        <button
          class="fb-submit-btn w-100"
          type="button"
          :disabled="isSubmitting"
          @click="submit"
        >
          {{ isSubmitting ? 'Submitting...' : 'Submit Feedback' }}
        </button>
      </div>
    </template>

  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { ArrowLeft, CheckCircle, Check } from 'lucide-vue'
  import { get, post } from '@/services/index.js'

  var FEEDBACK_ID = '725acf69-8cc2-4690-a339-f856b85ece34'

  export default {
    name: 'ConciergeFeedback',

    components: { ArrowLeft, CheckCircle, Check },

    metaInfo() {
      return {
        title: 'Feedback',
        titleTemplate: this.getMetaTitleTemplate(),
      }
    },

    data() {
      return {
        isLoading:    true,
        loadError:    '',
        screen:       'form',
        formData:     null,
        questions:    [],
        answers:      {},
        isSubmitting: false,
        submitError:  '',
      }
    },

    computed: {
      heroStyle() {
        var img = (this.formData && this.formData.image_url) || ''
        return {
          backgroundImage: 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.65) 100%), url(\'' + img + '\')',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }
      },
    },

    mounted() {
      var auth = this.getConciergeHomeAuth()
      if (auth.reservation_status === 'NEW_RESERVATION') {
        this.$router.replace({ name: 'ConciergeMainPage', query: { toast: 'Available once your reservation is active' } })
        return
      }
      this.fetchForm()
    },

    methods: {
      ...mapGetters(['getMerchant', 'getMetaTitleTemplate', 'getConciergeHomeAuth']),

      fetchForm() {
        this.isLoading = true
        this.loadError = ''
        var auth = this.getConciergeHomeAuth()
        var merchantId = this.getMerchant().id
        get('CORE_HOTEL', 'feedback-form/' + merchantId, { merchant_id: merchantId }, { type: 'concierge_token_direct', token: auth.token })
          .then(function(response) {
            var res = response.data
            if (res.success) {
              var d = res.data
              this.formData = d
              var sorted = (d.question_list || []).slice().sort(function(a, b) { return a.position - b.position })
              this.questions = sorted
              var answersInit = {}
              sorted.forEach(function(q) {
                if (q.answer_type === 'STAR') answersInit[q.id] = 0
                else if (q.answer_type === 'TEXT') answersInit[q.id] = ''
                else if (q.answer_type === 'MULTIPLE_CHOICE') answersInit[q.id] = []
                else answersInit[q.id] = ''
              })
              this.answers = answersInit
            } else {
              this.loadError = res.message || 'Failed to load feedback form.'
            }
            this.isLoading = false
          }.bind(this))
          .catch(function() {
            this.loadError = 'Failed to load feedback form. Please try again.'
            this.isLoading = false
          }.bind(this))
      },

      setAnswer(questionId, value) {
        this.$set(this.answers, questionId, value)
      },

      isMultiSelected(questionId, optId) {
        var arr = this.answers[questionId]
        return Array.isArray(arr) && arr.indexOf(optId) >= 0
      },

      toggleMulti(questionId, optId) {
        var current = this.answers[questionId] || []
        var idx = current.indexOf(optId)
        var next = current.slice()
        if (idx >= 0) next.splice(idx, 1)
        else next.push(optId)
        this.$set(this.answers, questionId, next)
      },

      submit() {
        this.submitError = ''
        var invalid = this.questions.filter(function(q) {
          if (!q.is_required) return false
          var ans = this.answers[q.id]
          if (q.answer_type === 'STAR') return !ans || ans < 1
          if (q.answer_type === 'TEXT') return !ans || !ans.trim()
          if (q.answer_type === 'MULTIPLE_CHOICE') return !ans || ans.length === 0
          return !ans
        }.bind(this))

        if (invalid.length > 0) {
          this.submitError = 'Please answer all required questions (marked with *).'
          return
        }

        this.isSubmitting = true
        var auth = this.getConciergeHomeAuth()
        var merchantId = this.getMerchant().id

        var questionAnswer = this.questions.map(function(q) {
          return { question: q.id, answer: this.answers[q.id] }
        }.bind(this))

        var payload = {
          user_detail: null,
          reservation_id: auth.reservation_id,
          question_answer: questionAnswer,
        }

        post('CORE_HOTEL', 'feedback-form/' + merchantId + '/answer?merchant_id=' + merchantId, payload, { type: 'concierge_token_direct', token: auth.token })
          .then(function(response) {
            var res = response.data
            if (res.success) {
              this.screen = 'success'
            } else {
              this.submitError = res.message || 'Failed to submit feedback.'
            }
            this.isSubmitting = false
          }.bind(this))
          .catch(function() {
            this.submitError = 'Something went wrong. Please try again.'
            this.isSubmitting = false
          }.bind(this))
      },
    },
  }
</script>

<style scoped>
.fb-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* ── Header ── */
.fb-header {
  padding: 14px 16px;
  background: #fff;
  border-bottom: 1px solid #E8EEF3;
  position: sticky;
  top: 0;
  z-index: 10;
  gap: 12px;
}

.fb-back-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1.5px solid #E0E8EF;
  background: #F5F7FA;
  color: #1C2B36;
  cursor: pointer;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
}

/* ── Center states ── */
.fb-center {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.fb-spinner-ring {
  width: 44px;
  height: 44px;
  border: 3px solid #E0E8EF;
  border-top-color: var(--merchant-primary-color, #2B5F7A);
  border-radius: 50%;
  animation: fbSpin 0.75s linear infinite;
}

@keyframes fbSpin {
  to { transform: rotate(360deg); }
}

.fb-error-text {
  font-size: 14px;
  color: #6B7886;
  line-height: 1.5;
}

/* ── Scroll area ── */
.fb-scroll {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

/* ── Hero ── */
.fb-hero {
  position: relative;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.fb-hero-overlay {
  position: absolute;
  inset: 0;
}

.fb-hero-body {
  position: relative;
  z-index: 1;
}

.fb-hero-title {
  font-size: 22px;
  font-weight: 900;
  color: #fff;
  letter-spacing: -0.3px;
  line-height: 1.2;
}

.fb-hero-desc {
  font-size: 13px;
  color: rgba(255,255,255,0.82);
  line-height: 1.55;
}

/* ── Question card ── */
.fb-question-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #E0E8EF;
  box-shadow: 0 1px 2px rgba(15,30,50,0.04), 0 4px 14px rgba(15,30,50,0.05);
}

.fb-question-label {
  font-size: 14.5px;
  font-weight: 700;
  color: #1C2B36;
  line-height: 1.4;
  margin-bottom: 0;
}

.fb-required {
  color: #C04040;
  font-weight: 800;
}

/* ── Star ── */
.fb-star-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-size: 28px;
  color: #E0E8EF;
  line-height: 1;
  transition: color .12s;
  -webkit-tap-highlight-color: transparent;
}

.fb-star-btn--active { color: var(--merchant-text-color, #C97B5C); }

/* ── Textarea ── */
.fb-textarea {
  width: 100%;
  border: 1.5px solid #E0E8EF;
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 13.5px;
  color: #1C2B36;
  background: #F8FAFC;
  resize: none;
  font-family: inherit;
  line-height: 1.5;
  outline: none;
  transition: border-color 0.15s;
}

.fb-textarea:focus {
  border-color: var(--merchant-primary-color, #2B5F7A);
  background: #fff;
}

/* ── Choice / Multiple choice buttons ── */
.fb-opt-btn {
  background: #F8FAFC;
  border: 1.5px solid #E0E8EF;
  border-radius: 12px;
  padding: 12px 14px;
  cursor: pointer;
  text-align: left;
  width: 100%;
  font-family: inherit;
  transition: background 0.12s, border-color 0.12s;
  -webkit-tap-highlight-color: transparent;
}

.fb-opt-btn--active {
  background: rgba(43,95,122,0.06);
  border-color: var(--merchant-primary-color, #2B5F7A);
}

.fb-opt-label {
  font-size: 14px;
  font-weight: 600;
  color: #1C2B36;
  line-height: 1.3;
}

/* Radio */
.fb-radio {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #CBD5DF;
  background: #fff;
  transition: border-color 0.12s;
}

.fb-radio--on {
  border-color: var(--merchant-primary-color, #2B5F7A);
  border-width: 6px;
}

/* Checkbox */
.fb-checkbox {
  width: 22px;
  height: 22px;
  border-radius: 7px;
  border: 2px solid #CBD5DF;
  background: #fff;
  flex-shrink: 0;
  transition: background 0.12s, border-color 0.12s;
}

.fb-checkbox--on {
  background: var(--merchant-primary-color, #2B5F7A);
  border-color: var(--merchant-primary-color, #2B5F7A);
  color: #fff;
}

/* ── Error ── */
.fb-submit-error {
  font-size: 13px;
  color: #C0392B;
  background: #FEF0EE;
  border: 1px solid #F5C6C0;
  border-radius: 10px;
  padding: 10px 12px;
  line-height: 1.5;
}

/* ── Sticky bar ── */
.fb-sticky-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(255,255,255,0.96);
  backdrop-filter: blur(10px);
  padding: 14px 16px 22px;
  border-top: 1px solid #E8EEF3;
  z-index: 20;
}

/* ── Submit button ── */
.fb-submit-btn {
  background: var(--merchant-primary-color, #2B5F7A);
  color: #fff;
  border: none;
  border-radius: 14px;
  padding: 15px 20px;
  font-size: 15px;
  cursor: pointer;
  font-family: Nunito-Bold, inherit;
  letter-spacing: 0.2px;
  -webkit-tap-highlight-color: transparent;
  transition: filter 0.12s;
}

.fb-submit-btn:disabled {
  background: #C8D4DC;
  cursor: default;
}

.fb-submit-btn:not(:disabled):active {
  filter: brightness(0.9);
}

/* ── Success screen ── */
.fb-success {
  flex: 1;
  padding-top: 72px;
  padding-bottom: 40px;
}

.fb-success-icon {
  width: 96px;
  height: 96px;
  border-radius: 999px;
  background: #E6F5EC;
  color: #1A8F44;
}

.fb-success-title {
  font-size: 24px;
  font-weight: 900;
  color: #1C2B36;
  letter-spacing: -0.4px;
}

.fb-success-sub {
  font-size: 14px;
  color: #6B7886;
  line-height: 1.5;
}
</style>



// WEBPACK FOOTER //
// src/pages/hotel/concierge/Feedback.vue